export type DockDirection = 'horizontal' | 'vertical';

export type DockPosition = 'top' | 'right' | 'bottom' | 'left';

/** Public configuration surface, mirrored 1:1 by the Settings app. */
export interface DockConfig {
  /** Base edge length of an item, in px. */
  size: number;
  /** Space between items, in px. Also the width of the hover-bridge. */
  gap: number;
  /** Inner padding of the dock shell, in px. */
  padding: number;
  /** Scale at the crest of the magnification wave. `1` disables magnification. */
  maxScale: number;
  /** Reach of the magnification wave, in px. */
  maxRange: number;
  /** Freeze all interaction. */
  disabled: boolean;
  /** Which axis items are laid out on. */
  direction: DockDirection;
  /** Which screen edge the dock is docked to (drives alignment + genie warp). */
  position: DockPosition;
  /** Promote items to their own layer while hovering. Costs memory, buys smoothness. */
  willChange: boolean;
  /** Easing used for the pointer-driven scale channel. */
  easing: string;
  /** Enable press-and-drag reordering. */
  sortable: boolean;
  /** Dragging an item out of the dock removes it. */
  allowDragDelete: boolean;
}

export const DEFAULT_DOCK_CONFIG: DockConfig = {
  size: 52,
  gap: 8,
  padding: 8,
  maxScale: 2,
  maxRange: 200,
  disabled: false,
  direction: 'horizontal',
  position: 'bottom',
  willChange: true,
  easing: 'cubic-bezier(0, 0.55, 0.45, 1)',
  sortable: true,
  allowDragDelete: true,
};

/** Imperative surface a `DockItem` hands to the `Dock` that owns it. */
export interface DockItemHandle {
  readonly el: HTMLLIElement;
  /** Unscaled size on the dock's active axis. */
  baseSize(direction: DockDirection): number;
  scale(): number;
  setScale(next: number): void;
  /** The launch bounce, mapped to the dock's edge. */
  bounce(direction: DockDirection, position: DockPosition): void;
}

export interface DockSortDetail {
  id: string;
  fromIndex: number;
  toIndex: number;
  /** Item ids in their new order. */
  ids: string[];
}

export interface DockDeleteDetail {
  id: string;
  index: number;
}
